import { Component, Input, Output, EventEmitter } from '@angular/core';
import * as Highcharts from 'highcharts';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';
import { timeseriesChartXaxis, reportChartLineOptions } from '../highcharts-options';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import * as moment from 'moment';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import {
  BaseReportComponent,
  ReportResponseWithName,
  ChartContextMenuItem,
} from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { SearchFilter } from 'ldt-people-api';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import * as dayjs from 'dayjs';
import { JobsMovesComparisonConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const REPORT_NAME = 'arrivals_departures';

@Component({
  selector: 'app-reports-jobs-moves-comparison',
  templateUrl: './jobs-moves-comparison.component.html',
  styleUrls: ['./jobs-moves-comparison.component.scss'],
  standalone: false,
})
export class JobsMovesComparisonComponent extends BaseReportComponent<JobsMovesComparisonConfig> {
  @Input() quickBuilds: QuickBuild[];
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() boxShadow: boolean = true;

  protected readonly CHART_ID = 'job-moves-comparison';
  protected readonly CHART_DATA_DESC = 'Job moves over time by company';

  chartId: string;
  subscriptions: Subscription = new Subscription();

  @Input()
  set selectedValueType(value: 'percentage' | 'number' | undefined) {
    if (value) {
      this._selectedValueType = value;
    }
  }
  get selectedValueType(): 'percentage' | 'number' {
    return this._selectedValueType;
  }

  @Input()
  set selectedSeries(value: 'arrivals' | 'departures' | 'both' | 'net' | undefined) {
    if (value) {
      this._selectedSeries = value;
    }
  }
  get selectedSeries(): 'arrivals' | 'departures' | 'both' | 'net' {
    return this._selectedSeries;
  }

  private _selectedValueType: 'percentage' | 'number' = 'percentage';
  private _selectedSeries: 'arrivals' | 'departures' | 'both' | 'net' = 'arrivals';

  valueTypeOptions = [
    { value: 'percentage', displayText: '%' },
    { value: 'number', displayText: '#' },
  ];

  seriesOptions = [
    { value: 'arrivals', displayText: 'Arrivals' },
    { value: 'departures', displayText: 'Departures' },
    { value: 'both', displayText: 'Both' },
    { value: 'net', displayText: 'Net' },
  ];
  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
    this.chartId = UniqueIdGenerator.generate();
    if (!this.dateFrom || !this.dateTo) {
      this.dateFrom = moment().subtract(2, 'year').startOf('month').format('YYYY-MM');
      this.dateTo = moment().startOf('month').format('YYYY-MM');
    }

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedValueType', () => this._selectedValueType);
    this.registerConfigProperty('selectedSeries', () => this._selectedSeries);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartLineOptions)
    );
    this.chart.update(JobMovesChartNoSeries, true, true);
    Log.d('JobsMovesComparisonComponent initializeChart', this.chart);
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    Log.d(
      'JobsMovesComparisonComponent getChartData',
      this.quickBuilds,
      this.dateFrom,
      this.dateTo
    );
    if (!this.quickBuilds) return;
    // Set this primarily for the chart subtitles to generate in base-report
    this.quickBuild = this.quickBuilds[0];

    this.showLoading();

    // Get the data for each quick build (Company), joining together so we can provide the collected data to the AI
    this.subscriptions.add(
      forkJoin(
        this.quickBuilds
          .filter((quickBuild) => quickBuild)
          .map((quickBuild) => {
            return this.addQuickbuildToChart(quickBuild);
          })
      ).subscribe({
        next: (data: (ReportResponseWithName | null)[]) => {
          this.chartLoadingComplete();
          Log.d('JobsMovesComparisonComponent getChartData next');
        },
        error: (error) => {
          Log.e('JobsMovesComparisonComponent getChartData error', error);
          this.showChartError(error);
        },
      })
    );
  }

  // Add a single company to the chart
  addQuickbuildToChart(quickBuild: QuickBuild): Observable<ReportResponseWithName | null> {
    Log.d(
      'JobsMovesComparisonComponent addQuickbuildToChart',
      quickBuild,
      this.dateFrom,
      this.dateTo
    );

    // Remove the company from the chart if it already exists
    this.removeQuickbuildFromChart(quickBuild.id, quickBuild.name);

    // We need the arrivals/departures, and for pct values we also need the total counts
    const reportParams = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: [],
    };
    const movesReport = this.reportService.getReportDataWithReportDetails(
      this.reportId,
      REPORT_NAME,
      reportParams,
      this.reportTarget,
      quickBuild
    );

    const totalsReport = this.reportService.getReportDataWithReportDetails(
      this.reportId,
      'demographics',
      reportParams,
      this.reportTarget,
      quickBuild
    );

    // Get both datasets and generate the chart series
    const sub = forkJoin([movesReport, totalsReport]).pipe(
      tap(([movesData, totalsData]) => {
        this.generateChartSeries(
          quickBuild.id,
          quickBuild.name,
          movesData.data.arrivals_departures,
          totalsData.data.demographics
        );
      }),
      map((data) => ({
        name: quickBuild.name,
        data: data[0], // Just send the moves data to the AI
      })),
      catchError((error) => {
        Log.e('JobsMovesComparisonComponent addQuickbuildToChart error', error);
        throw error;
      })
    );

    return sub;
  }

  async resetChartData() {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    await this.removeAllSeriesFromChart();
    switch (this._selectedValueType) {
      case 'percentage':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Pct Change' },
          },
        });
        break;
      case 'number':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Number of Employees' },
          },
        });
        break;
    }
  }

  // Generate the chart series for a single company given the required data
  generateChartSeries(
    quickBuildId: string,
    quickBuildName: string,
    movesData: any,
    totalsData: any
  ): void {
    Log.d('JobsMovesComparisonComponent generateChartSeries', movesData, totalsData);

    if (movesData.length === 0) {
      return;
    }

    let processed = movesData.reduce((acc: any, d: any) => {
      const keyVal = 'Total';
      if (!acc[keyVal]) {
        acc[keyVal] = {};
      }

      const dateVal = d.date;

      if (!acc[keyVal][dateVal]) {
        acc[keyVal][dateVal] = {
          count_arrivals: 0,
          count_departures: 0,
        };
      }
      acc[keyVal][dateVal].count_arrivals += d.arrivals;
      acc[keyVal][dateVal].count_departures += d.departures;

      return acc;
    }, {});

    // Set the data point based on the selected chart options
    let data = Object.entries(processed.Total).map(([key, value]: any) => {
      let seriesValue;
      if (this._selectedSeries === 'arrivals') {
        seriesValue = value.count_arrivals;
      } else if (this._selectedSeries === 'departures') {
        seriesValue = value.count_departures;
      } else if (this._selectedSeries === 'net') {
        seriesValue = value.count_arrivals - value.count_departures;
      } else {
        // 'both'
        seriesValue = value.count_arrivals + value.count_departures;
      }

      const totalCount = totalsData.find((d: any) => d.date === key)?.count_employees || 1;

      if (this._selectedValueType === 'percentage') {
        return [new Date(key).valueOf(), (seriesValue / totalCount) * 100];
      } else {
        return [new Date(key).valueOf(), seriesValue];
      }
    });

    Log.d('JobsMovesComparisonComponent generateChartSeries data:', data);

    this.addSeriesToChart({
      custom: { id: quickBuildId },
      id: quickBuildId,
      name: quickBuildName,
      data: data,
      type: 'spline',
      colorIndex: this.quickBuilds.findIndex((qb) => qb.id === quickBuildId),
    });
  }

  removeQuickbuildFromChart(quickBuildId: string, quickBuildName: string) {
    if (!this.chart) return;
    this.chart.series.forEach((series) => {
      if (series.options.custom?.id === quickBuildId) {
        series.remove();
      }
    });
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const date = dayjs(point.x as number);
    const series = point.series;
    const quickBuildId = series.options.custom?.id;
    const quickBuild = this.quickBuilds.find((qb) => qb.id === quickBuildId);
    if (!quickBuild) {
      Log.s('JobsMovesComparisonComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
        seriesCustomOptions: series.options.custom ? JSON.stringify(series.options.custom) : '',
        quickBuilds: JSON.stringify(this.quickBuilds),
      });
      return [];
    }

    Log.d('JobsMovesComparisonComponent getContextMenuItems', point);

    const qbCopy = QuickBuildService.copyQuickBuild(quickBuild);

    const startDateFilter: UIFilter = {
      filter: {
        field: 'jobs.started_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_to: date.toISOString(),
        date_from: date.toISOString(),
      },
    };
    const endDateFilter: UIFilter = {
      filter: {
        field: 'jobs.ended_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
        date_to: date.toISOString(),
      },
    };

    // If the user has only arrivals or departures selected, we'll just use that data point. If they have both or net we'll show both options
    const items: ChartContextMenuItem[] = [];

    if (this._selectedSeries !== 'departures') {
      items.push({
        label: `View People that arrived at ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [startDateFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = PositionStatus.First;
          qbCopy.name = `People that arrived at ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      });
    }

    if (this._selectedSeries !== 'arrivals') {
      items.push({
        label: `View People that departed ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [endDateFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = PositionStatus.Last;
          qbCopy.name = `People that departed ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      });
    }

    return items;
  }

  onValueTypeChanged(value: string) {
    this._selectedValueType = value as 'percentage' | 'number';
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  onSeriesChanged(value: string) {
    this._selectedSeries = value as 'arrivals' | 'departures' | 'both' | 'net';
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  getFieldDisplayName(field: string): string {
    return getPeopleColumnByName(field).displayName;
  }
}

const JobMovesChartNoSeries: Highcharts.Options = {
  title: {
    text: 'Job Moves',
  },
  xAxis: timeseriesChartXaxis,
  yAxis: [
    {
      title: {
        text: 'Pct Change',
      },
    },
  ],
};
